/**
 * ZeropsServiceCardBase
 *
 * Abstract base class for zagent and zcde service cards.
 * Provides common streams and functionality to eliminate code duplication.
 */

import { inject, computed, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { GeneralTranslations } from '@zerops/zerops/app';
import { ServiceStackEntity } from '@zerops/zerops/core/service-stack-base';
import { UserDataEntity } from '@zerops/zerops/core/user-data-base';
import { ProcessEntity } from '@zerops/zerops/core/process-base';
import { ProjectEntity, PROJECT_BASE_FEATURE_NAME } from '@zerops/zerops/core/project-base';
import { ServiceStack } from '@zerops/models/service-stack';
import { Observable, combineLatest } from 'rxjs';
import { map, filter, switchMap, distinctUntilChanged, withLatestFrom } from 'rxjs/operators';
import {
  isServiceProcessing,
  createPublicAccessLoadingStream,
  createBuildProcessStreams,
  detectPublicAccess,
  extractServiceMetadata
} from '../../zerops-services.utils';
import { ZeropsPublicAccess, ZeropsServiceMetadata } from '../../zerops-services.model';

/**
 * Base class for Zerops service cards (zagent, zcde)
 * Provides common streams and functionality
 */
export abstract class ZeropsServiceCardBase extends ZefReactiveComponent {
  // # Deps
  #serviceStackEntity = inject(ServiceStackEntity);
  #userDataEntity = inject(UserDataEntity);
  #processEntity = inject(ProcessEntity);
  #projectEntity = inject(ProjectEntity);

  // # Abstract - must be implemented by subclasses
  abstract serviceStackId: Signal<string | undefined>;
  abstract readonly userDataTag: string; // e.g., 'zagent' or 'zcde'

  // # Common Streams
  #serviceStackId$ = computed(() => this.serviceStackId());

  #serviceStackIdStream$ = toObservable(this.#serviceStackId$).pipe(
    distinctUntilChanged(),
    filter((id): id is string => !!id)
  );

  serviceStack$ = this.#serviceStackIdStream$.pipe(
    switchMap((id) => this.#serviceStackEntity.entityById$(id)),
    filter((stack): stack is ServiceStack => !!stack)
  );

  serviceActive$ = this.serviceStack$.pipe(
    map((stack) => stack?.status === 'ACTIVE')
  );

  serviceProcessing$ = this.serviceStack$.pipe(
    map((stack) => stack ? isServiceProcessing(stack.status) : false)
  );

  projectId$ = this.serviceStack$.pipe(
    map((stack) => stack?.projectId),
    filter((id): id is string => !!id),
    distinctUntilChanged()
  );

  // User data for this service
  userData$ = this.serviceStack$.pipe(
    filter((stack) => !!stack),
    switchMap((stack) => this.#userDataEntity.list$({
      name: `${PROJECT_BASE_FEATURE_NAME}-${this.userDataTag}`,
      id: stack.projectId
    }).pipe(
      map((userData) => userData?.filter((item) => item.serviceStackId === stack.id) ?? [])
    ))
  );

  // Project service stacks for hostname resolution
  projectServiceStacks$ = this.projectId$.pipe(
    switchMap((projectId) => this.#projectEntity.entityByIdWithServiceStacks$(projectId)),
    map((project) => project?.serviceStacks ?? [])
  );

  // Metadata extracted from user data
  metadata$: Observable<ZeropsServiceMetadata> = this.userData$.pipe(
    map((userData) => extractServiceMetadata(userData))
  );

  // Public access configuration
  publicAccess$: Observable<ZeropsPublicAccess> = combineLatest([
    this.serviceStack$,
    this.userData$
  ]).pipe(
    map(([ stack, userData ]) => detectPublicAccess(stack, userData))
  );

  // Process tracking
  #allProcesses$ = this.#processEntity.list$().pipe(
    withLatestFrom(this.serviceStack$),
    filter(([ processes, stack ]) => !!processes && !!stack),
    map(([ processes, stack ]) => processes.filter((p) => p.serviceStackId === stack.id))
  );

  enablePublicAccessLoading$ = createPublicAccessLoadingStream(this.#allProcesses$);

  buildProcesses = createBuildProcessStreams(this.#allProcesses$);

  hasBuildProcesses$ = combineLatest([
    this.buildProcesses.runningBuild$,
    this.buildProcesses.pendingBuild$
  ]).pipe(
    map(([ running, pending ]) => !!(running || pending))
  );

  // Translations
  generalTranslations$ = this.translate$<GeneralTranslations>('general');
}
